import React, { useState } from 'react';
import {
  Container,
  Typography,
  Card,
  Box,
  Stack,
  TextField,
  Button,
  Paper,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { keyframes } from '@emotion/react';
import {
  Email,
  Phone,
  LocationOn,
  WhatsApp,
  Send,
  CheckCircle
} from '@mui/icons-material';
import { useSettings } from '../context/SettingsContext';
import contactService from '../services/contactService';

// Animation slide pour le titre
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Contact = () => {
  const { settings } = useSettings();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: '' // Honeypot field - les robots remplissent ce champ, les humains non
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const contactInfo = {
    email: settings.contact_email || 'contact@tchambamarketplace.com',
    phone: settings.contact_phone || '+228 XX XX XX XX',
    whatsapp: settings.contact_whatsapp || '+228 XX XX XX XX',
    address: settings.contact_address || 'Tchamba, Togo',
    openingHours: settings.contact_opening_hours || 'Lundi - Vendredi: 8h00 - 18h00\nSamedi: 9h00 - 16h00\nDimanche: Fermé'
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Le sujet est requis';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await contactService.sendContactMessage(formData);
      
      if (response.success) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          website: ''
        });
        setErrors({});
        
        // Réinitialiser le message de succès après 5 secondes
        setTimeout(() => {
          setSubmitted(false);
        }, 5000);
      } else {
        setErrorMessage(response.message || 'Erreur lors de l\'envoi du message. Veuillez réessayer.');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erreur lors de l\'envoi du message. Veuillez réessayer.';
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppClick = () => {
    const message = `Bonjour, je souhaite prendre contact avec vous.`;
    const whatsappUrl = `https://wa.me/${contactInfo.whatsapp}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handlePhoneClick = () => {
    window.open(`tel:${contactInfo.phone}`, '_self');
  };

  const handleEmailClick = () => {
    window.open(`mailto:${contactInfo.email}`, '_self');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography 
          variant="h2" 
          sx={{ 
            fontWeight: 700,
            animation: `${slideIn} 0.8s ease-out`,
            mb: 2
          }}
        >
          Contactez-nous
        </Typography>
        <Box
          sx={{
            width: 80,
            height: 4,
            background: 'linear-gradient(90deg, #f97316, #ef4444, #ec4899)',
            mx: 'auto',
            mb: 3,
            borderRadius: 2,
            animation: `${slideIn} 0.8s ease-out 0.2s both`
          }}
        />
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ 
            maxWidth: 600, 
            mx: 'auto',
            animation: `${slideIn} 0.8s ease-out 0.4s both`
          }}
        >
          Notre équipe est là pour vous accompagner. N'hésitez pas à nous contacter pour toute question.
        </Typography>
      </Box>

      {/* Main Content: Contact Info + Form */}
      <Box 
        sx={{ 
          mb: 12, 
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4
        }}
      >
        {/* Section gauche - Coordonnées */}
        <Box sx={{ width: { xs: '100%', md: '40%' }, flexShrink: 0 }}>
          <Card sx={{ height: '100%', p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
              Nos Coordonnées
            </Typography>
            
            <List sx={{ '& .MuiListItem-root': { px: 0, py: 2 } }}>
              <ListItem>
                <ListItemIcon>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}
                  >
                    <Email />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Button
                      onClick={handleEmailClick}
                      sx={{
                        textTransform: 'none',
                        color: 'text.primary',
                        fontWeight: 600,
                        p: 0,
                        '&:hover': {
                          color: 'primary.main',
                          bgcolor: 'transparent'
                        }
                      }}
                    >
                      {contactInfo.email}
                    </Button>
                  }
                  secondary="Envoyez-nous un email"
                />
              </ListItem>

              <Divider />

              <ListItem>
                <ListItemIcon>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: 'success.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}
                  >
                    <Phone />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Button
                      onClick={handlePhoneClick}
                      sx={{
                        textTransform: 'none',
                        color: 'text.primary',
                        fontWeight: 600,
                        p: 0,
                        '&:hover': {
                          color: 'primary.main',
                          bgcolor: 'transparent'
                        }
                      }}
                    >
                      {contactInfo.phone}
                    </Button>
                  }
                  secondary="Appelez-nous"
                />
              </ListItem>

              <Divider />

              <ListItem>
                <ListItemIcon>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: '#25D366',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}
                  >
                    <WhatsApp />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Button
                      onClick={handleWhatsAppClick}
                      sx={{
                        textTransform: 'none',
                        color: 'text.primary',
                        fontWeight: 600,
                        p: 0,
                        '&:hover': {
                          color: '#25D366',
                          bgcolor: 'transparent'
                        }
                      }}
                    >
                      {contactInfo.whatsapp}
                    </Button>
                  }
                  secondary="Chattez avec nous"
                />
              </ListItem>

              <Divider />

              <ListItem>
                <ListItemIcon>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: 'error.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}
                  >
                    <LocationOn />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={contactInfo.address}
                  secondary="Notre adresse"
                />
              </ListItem>
            </List>

            <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Heures d'ouverture:</strong>
                <br />
                {contactInfo.openingHours.split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < contactInfo.openingHours.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </Typography>
            </Box>
          </Card>
        </Box>

        {/* Section droite - Formulaire */}
        <Box sx={{ width: { xs: '100%', md: '58%' }, flexShrink: 0 }}>
          <Card sx={{ p: 3, height: '100%' }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
              Envoyez-nous un message
            </Typography>

            {submitted && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Votre message a été envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.
              </Alert>
            )}

            {errorMessage && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {errorMessage}
              </Alert>
            )}

            <form onSubmit={handleSubmit} style={{ position: 'relative' }}>
              {/* Honeypot field - invisible pour les humains, visible pour les robots */}
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleChange}
                autoComplete="off"
                tabIndex={-1}
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: '1px',
                  height: '1px',
                  opacity: 0,
                  pointerEvents: 'none',
                  zIndex: -1
                }}
                aria-hidden="true"
              />
              
              <Stack spacing={3}>
                <TextField
                  name="name"
                  label="Nom complet"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  fullWidth
                  required
                  variant="outlined"
                />

                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  fullWidth
                  required
                  variant="outlined"
                />

                <TextField
                  name="subject"
                  label="Sujet"
                  value={formData.subject}
                  onChange={handleChange}
                  error={!!errors.subject}
                  helperText={errors.subject}
                  fullWidth
                  required
                  variant="outlined"
                />

                <TextField
                  name="message"
                  label="Message"
                  value={formData.message}
                  onChange={handleChange}
                  error={!!errors.message}
                  helperText={errors.message}
                  fullWidth
                  required
                  multiline
                  rows={6}
                  variant="outlined"
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={<Send />}
                  fullWidth
                  disabled={loading || submitted}
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(90deg, #9b5de5 0%, #f15bb5 100%)',
                    '&:hover': {
                      background: 'linear-gradient(90deg, #f15bb5 0%, #9b5de5 100%)',
                    },
                    '&:disabled': {
                      background: 'linear-gradient(90deg, #9b5de5 0%, #f15bb5 100%)',
                      opacity: 0.6
                    }
                  }}
                >
                  {loading ? 'Envoi en cours...' : submitted ? 'Message envoyé !' : 'Envoyer le message'}
                </Button>
              </Stack>
            </form>

            {/* Message d'information RGPD */}
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  fontSize: '0.75rem',
                  lineHeight: 1.6,
                  display: 'block',
                  textAlign: 'center'
                }}
              >
                Les informations recueillies à partir de ce formulaire sont transmises aux équipes administratives de Tchamba Marketplace dans le but d'apporter une réponse à votre message. Vos données personnelles ne sont en aucun cas cédées ou vendues à des tiers.
              </Typography>
            </Box>

          </Card>
        </Box>
      </Box>

      {/* Carte Google Maps */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
          Notre Localisation
        </Typography>
        <Paper
          sx={{
            height: 400,
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: 3
          }}
        >
          <iframe
            src={`https://www.google.com/maps?q=${encodeURIComponent(contactInfo.address)}&output=embed`}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={`Carte de localisation - ${contactInfo.address}`}
          />
        </Paper>
      </Box>
    </Container>
  );
};

export default Contact;
